import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { getTables, createTable, updateTable, deleteTable, getTableQRImage } from '../api/restaurant';
import { Plus, Edit, Trash2, QrCode, Users, Download, ArrowLeft, Unlink, Check, Power } from 'lucide-react';
import api from '../api/axios';

const SHAPES = {
  carre: { label: 'Carre', w: 120, h: 120 },
  rond: { label: 'Rond', w: 120, h: 120 },
  petit_carre: { label: 'Petit carre', w: 90, h: 90 },
  rectangulaire: { label: 'Rectangulaire', w: 160, h: 100 },
};

const SHAPE_OPTIONS = [
  { value: 'carre', label: 'Carre' },
  { value: 'rond', label: 'Rond' },
  { value: 'petit_carre', label: 'Petit carre' },
  { value: 'rectangulaire', label: 'Rectangulaire' },
];

const TableVisual = ({ table, isAdmin, canToggle, onDragStart, onDragOver, onDrop, onEdit, onDelete, onShowQR, onUnmerge, onToggleActive, mergedGroup }) => {
  const shape = table.shape || 'carre';
  const isActive = table.active;
  const isMerged = !!table.merged_with;
  const covers = mergedGroup ? mergedGroup.reduce((s, t) => s + t.covers, 0) : table.covers;
  const dim = SHAPES[shape] || SHAPES.carre;

  // Colors based on state
  const bg = isActive ? '#d4a574' : '#e8ddd4';
  const border = isActive ? '#8b5e3c' : '#c4b5a8';
  const textColor = isActive ? '#3d1f0a' : '#7a6b5d';

  const renderShape = () => {
    const commonStyle = {
      width: dim.w,
      height: dim.h,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: isAdmin && table.modulable && !isMerged ? 'grab' : 'default',
      transition: 'all 0.2s ease',
      position: 'relative',
      border: `3px solid ${border}`,
      background: `linear-gradient(135deg, ${bg}, ${bg}dd)`,
      boxShadow: isActive ? '0 4px 12px rgba(139,94,60,0.3)' : '0 2px 6px rgba(0,0,0,0.08)',
    };

    if (shape === 'rond') {
      commonStyle.borderRadius = '50%';
    } else if (shape === 'petit_carre') {
      commonStyle.borderRadius = '10px';
    } else if (shape === 'rectangulaire') {
      commonStyle.borderRadius = '12px';
    } else {
      commonStyle.borderRadius = '12px';
    }

    // Draw "chairs" around the table
    const chairCount = Math.min(covers, 8);

    return (
      <div className="relative" style={{ width: dim.w + 40, height: dim.h + 40 }}>
        {/* Chairs */}
        {renderChairs(chairCount, dim, shape)}
        {/* Table surface */}
        <div
          style={{ ...commonStyle, position: 'absolute', left: 20, top: 20 }}
          draggable={isAdmin && table.modulable}
          onDragStart={(e) => onDragStart(e, table)}
          onDragOver={onDragOver}
          onDrop={(e) => onDrop(e, table)}
          data-testid={`table-visual-${table.table_number}`}
        >
          <span style={{ fontSize: table.name && !isMerged && table.name.length > 4 ? 14 : 22, fontWeight: 800, color: textColor, lineHeight: 1, textAlign: 'center', wordBreak: 'break-word', maxWidth: dim.w - 16 }}>
            {isMerged ? mergedGroup.map(t => t.table_number).join('+') : (table.name || table.table_number)}
          </span>
          {table.name && !isMerged && (
            <span style={{ fontSize: 9, color: textColor, opacity: 0.5, marginTop: 1 }}>
              #{table.table_number}
            </span>
          )}
          <span style={{ fontSize: 10, color: textColor, opacity: 0.7, marginTop: 2 }}>
            {covers} couv.
          </span>
          {isActive && (
            <div style={{ position: 'absolute', top: -6, right: -6, width: 14, height: 14, borderRadius: '50%', background: '#22c55e', border: '2px solid #fff' }} />
          )}
          {!isActive && (
            <div style={{ position: 'absolute', top: -6, right: -6, fontSize: 8, fontWeight: 700, color: '#fff', background: '#9ca3af', borderRadius: 4, padding: '1px 4px', border: '1px solid #fff' }}>OFF</div>
          )}
          {!table.modulable && (
            <div style={{ position: 'absolute', bottom: -4, right: -4, width: 12, height: 12, borderRadius: '50%', background: '#ef4444', border: '2px solid #fff' }} title="Non modulable" />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center gap-1 group" data-testid={`table-container-${table.table_number}`}>
      {renderShape()}
      {/* Actions under table */}
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity mt-1">
        {canToggle && (
          <button
            onClick={() => onToggleActive(table)}
            className={`p-1 rounded transition-colors ${isActive ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
            data-testid={`toggle-active-btn-${table.table_number}`}
            title={isActive ? 'Desactiver' : 'Activer'}
          >
            <Power size={13} />
          </button>
        )}
        <button onClick={() => onShowQR(table)} className="p-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors" data-testid={`qr-btn-${table.table_number}`} title="QR Code">
          <QrCode size={13} />
        </button>
        {isAdmin && (
          <>
            <button onClick={() => onEdit(table)} className="p-1 rounded bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors" data-testid={`edit-btn-${table.table_number}`} title="Modifier">
              <Edit size={13} />
            </button>
            <button onClick={() => onDelete(table.id, table.table_number)} className="p-1 rounded bg-red-100 text-red-700 hover:bg-red-200 transition-colors" data-testid={`delete-btn-${table.table_number}`} title="Supprimer">
              <Trash2 size={13} />
            </button>
          </>
        )}
        {isMerged && isAdmin && (
          <button onClick={() => onUnmerge(table.table_number)} className="p-1 rounded bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors" data-testid={`unmerge-btn-${table.table_number}`} title="Delier">
            <Unlink size={13} />
          </button>
        )}
      </div>
    </div>
  );
};

const renderChairs = (count, dim, shape) => {
  const chairs = [];
  const cx = dim.w / 2 + 20;
  const cy = dim.h / 2 + 20;
  const rx = dim.w / 2 + 14;
  const ry = dim.h / 2 + 14;

  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2 - Math.PI / 2;
    const x = cx + Math.cos(angle) * rx - 7;
    const y = cy + Math.sin(angle) * ry - 7;
    chairs.push(
      <div key={i} style={{
        position: 'absolute', left: x, top: y, width: 14, height: 14,
        borderRadius: shape === 'rond' ? '50%' : '4px',
        background: '#a0845c', border: '2px solid #8b6f47', opacity: 0.6,
      }} />
    );
  }
  return chairs;
};

const TablesManagement = () => {
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedTableQR, setSelectedTableQR] = useState(null);
  const [dragOverTable, setDragOverTable] = useState(null);
  const [formData, setFormData] = useState({ table_number: '', name: '', covers: 4, shape: 'carre', modulable: true });
  const staffRole = localStorage.getItem('staffRole');
  const isAdmin = staffRole === 'admin' || staffRole === 'webmaster';
  const canToggle = staffRole === 'serveur' || staffRole === 'admin' || staffRole === 'webmaster';
  const dragRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('staffToken');
    if (!token) { navigate('/staff/login'); return; }
    fetchTables();
  }, [navigate]);

  const fetchTables = async () => {
    try {
      const data = await getTables();
      setTables(data);
    } catch { toast({ title: 'Erreur', description: 'Impossible de charger les tables', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  const handleAdd = () => {
    setEditingTable(null);
    setFormData({ table_number: Math.max(0, ...tables.map(t => t.table_number)) + 1, name: '', covers: 4, shape: 'carre', modulable: true });
    setIsDialogOpen(true);
  };

  const handleEdit = (table) => {
    setEditingTable(table);
    setFormData({ table_number: table.table_number, name: table.name || '', covers: table.covers, shape: table.shape || 'carre', modulable: table.modulable !== false });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTable) {
        await updateTable(editingTable.id, { name: formData.name || null, covers: formData.covers, shape: formData.shape, modulable: formData.modulable });
      } else {
        await createTable({ table_number: formData.table_number, name: formData.name || null, covers: formData.covers, shape: formData.shape, modulable: formData.modulable });
      }
      toast({ title: editingTable ? 'Table modifiee' : 'Table creee' });
      setIsDialogOpen(false);
      fetchTables();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail || 'Erreur', variant: 'destructive' });
    }
  };

  const handleDelete = async (tableId, tableNumber) => {
    if (!window.confirm(`Supprimer la table ${tableNumber} ?`)) return;
    try {
      await deleteTable(tableId);
      toast({ title: 'Table supprimee' });
      fetchTables();
    } catch { toast({ title: 'Erreur', variant: 'destructive' }); }
  };

  const handleDragStart = (e, table) => {
    dragRef.current = table;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetTable) => {
    e.preventDefault();
    const sourceTable = dragRef.current;
    dragRef.current = null;
    setDragOverTable(null);

    if (!sourceTable || sourceTable.table_number === targetTable.table_number) return;
    if (!sourceTable.modulable || !targetTable.modulable) {
      toast({ title: 'Non modulable', description: 'Une des tables n\'est pas modulable', variant: 'destructive' });
      return;
    }

    // Shape compatibility check (carre/petit_carre/rectangulaire can merge, rond only with rond)
    const squareFamily = ['carre', 'petit_carre', 'rectangulaire'];
    const srcShape = sourceTable.shape || 'carre';
    const tgtShape = targetTable.shape || 'carre';
    const srcIsSquare = squareFamily.includes(srcShape);
    const tgtIsSquare = squareFamily.includes(tgtShape);
    if (!(srcIsSquare && tgtIsSquare) && srcShape !== tgtShape) {
      toast({ title: 'Formes incompatibles', description: 'Les tables carrees/rectangulaires se fusionnent entre elles, les rondes uniquement entre elles.', variant: 'destructive' });
      return;
    }

    // Collect all table numbers (including any existing merged groups)
    const allNumbers = new Set();
    allNumbers.add(sourceTable.table_number);
    allNumbers.add(targetTable.table_number);
    if (sourceTable.merged_with) sourceTable.merged_with.forEach(n => allNumbers.add(n));
    if (targetTable.merged_with) targetTable.merged_with.forEach(n => allNumbers.add(n));

    try {
      await api.post('/tables/merge', { table_numbers: [...allNumbers] });
      const sorted = [...allNumbers].sort((a, b) => a - b);
      toast({ title: 'Tables fusionnees', description: `Tables ${sorted.join('+')} combinees` });
      fetchTables();
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Impossible de fusionner', variant: 'destructive' });
    }
  };

  const handleUnmerge = async (tableNumber) => {
    try {
      await api.post(`/tables/unmerge/${tableNumber}`);
      toast({ title: 'Tables separees' });
      fetchTables();
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Erreur', variant: 'destructive' });
    }
  };

  const handleToggleActive = async (table) => {
    try {
      const res = await api.put(`/tables/${table.id}/toggle-active`);
      toast({ title: res.data.active ? 'Table activee' : 'Table desactivee', description: `Table ${table.table_number} ${res.data.active ? 'activee' : 'desactivee'}` });
      fetchTables();
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Impossible de changer le statut', variant: 'destructive' });
    }
  };

  const showQRCode = (table) => { setSelectedTableQR(table); setQrDialogOpen(true); };
  const downloadQRCode = (table) => {
    const link = document.createElement('a');
    link.href = getTableQRImage(table.id);
    link.download = `table-${table.table_number}-qr.png`;
    link.click();
  };

  // Group merged tables: only show group once (by the lowest table number)
  const mergedGroups = {};
  tables.forEach(t => {
    if (t.merged_with && t.merged_with.length > 0) {
      const key = [...t.merged_with].sort().join('-');
      if (!mergedGroups[key]) mergedGroups[key] = [];
      mergedGroups[key].push(t);
    }
  });

  const displayedMergedKeys = new Set();
  const visibleTables = [];
  tables.forEach(t => {
    if (t.merged_with && t.merged_with.length > 0) {
      const key = [...t.merged_with].sort().join('-');
      if (!displayedMergedKeys.has(key)) {
        displayedMergedKeys.add(key);
        visibleTables.push({ ...t, _mergedGroup: mergedGroups[key], _isMergedLeader: true });
      }
    } else {
      visibleTables.push(t);
    }
  });

  const totalCovers = tables.reduce((s, t) => s + (t.covers || 0), 0);
  const activeTables = tables.filter(t => t.active).length;
  const inactiveTables = tables.filter(t => !t.active).length;
  const activeCovers = tables.filter(t => t.active).reduce((s, t) => s + (t.covers || 0), 0);

  if (loading) return <div className="flex items-center justify-center min-h-screen text-stone-400">Chargement...</div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f1ed] to-[#ebe4db]">
      {/* Header */}
      <div className="bg-[#3d2817] text-white py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold" data-testid="tables-title">Plan des Tables</h1>
            <p className="text-xs text-stone-300 mt-0.5">
              {tables.length} tables | {activeCovers} couverts dispo | {activeTables} active{activeTables > 1 ? 's' : ''}{inactiveTables > 0 ? ` | ${inactiveTables} inactive${inactiveTables > 1 ? 's' : ''}` : ''}
            </p>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <Button onClick={handleAdd} size="sm" className="bg-green-600 hover:bg-green-700 text-xs" data-testid="add-table-btn">
                <Plus size={14} className="mr-1" /> Ajouter
              </Button>
            )}
            <Button onClick={() => navigate('/staff/dashboard')} size="sm" className="bg-red-600 hover:bg-red-700 text-xs">
              <ArrowLeft size={14} className="mr-1" /> Dashboard
            </Button>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex flex-wrap gap-4 text-xs text-stone-500">
          <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-green-500" /> Active</span>
          <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-stone-300" /> Inactive</span>
          <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-red-500" /> Non modulable</span>
          {isAdmin && <span className="text-stone-400 italic">Glissez une table sur une autre pour les fusionner</span>}
        </div>
      </div>

      {/* Tables visual layout */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="bg-[#e8ddd4]/40 border-2 border-dashed border-stone-300 rounded-2xl p-8 min-h-[500px]" data-testid="tables-floor">
          <div className="flex flex-wrap gap-8 justify-center">
            {visibleTables.map(table => (
              <TableVisual
                key={table.id}
                table={table}
                isAdmin={isAdmin}
                canToggle={canToggle}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onShowQR={showQRCode}
                onUnmerge={handleUnmerge}
                onToggleActive={handleToggleActive}
                mergedGroup={table._mergedGroup || null}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Edit/Add Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent data-testid="table-dialog">
          <DialogHeader>
            <DialogTitle>{editingTable ? `Modifier Table ${editingTable.table_number}` : 'Nouvelle table'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            {!editingTable && (
              <div className="space-y-1.5">
                <Label htmlFor="table_number" className="text-sm">Numero de table</Label>
                <Input id="table_number" type="number" value={formData.table_number}
                  onChange={(e) => setFormData({ ...formData, table_number: parseInt(e.target.value) })} required data-testid="input-table-number" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="table_name" className="text-sm">Nom de la table <span className="text-stone-400 text-xs">(optionnel)</span></Label>
              <Input id="table_name" type="text" value={formData.name} placeholder="Ex: Terrasse, VIP, Bar..."
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="input-table-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="covers" className="text-sm">Nombre de couverts</Label>
              <Input id="covers" type="number" min="1" max="12" value={formData.covers}
                onChange={(e) => setFormData({ ...formData, covers: parseInt(e.target.value) })} required data-testid="input-covers" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Forme de la table</Label>
              <div className="grid grid-cols-4 gap-2" data-testid="shape-selector">
                {SHAPE_OPTIONS.map(opt => (
                  <button key={opt.value} type="button"
                    onClick={() => setFormData({ ...formData, shape: opt.value })}
                    className={`p-3 rounded-lg border-2 text-center transition-all ${formData.shape === opt.value ? 'border-[#8B0000] bg-red-50' : 'border-stone-200 hover:border-stone-400'}`}
                    data-testid={`shape-${opt.value}`}>
                    <div className="mx-auto mb-1" style={{
                      width: opt.value === 'petit_carre' ? 28 : opt.value === 'rectangulaire' ? 40 : 32,
                      height: opt.value === 'rectangulaire' ? 24 : opt.value === 'petit_carre' ? 22 : 32,
                      borderRadius: opt.value === 'rond' ? '50%' : opt.value === 'petit_carre' ? '4px' : '6px',
                      background: formData.shape === opt.value ? '#8B0000' : '#c4b5a8',
                      transition: 'all 0.15s',
                    }} />
                    <span className="text-[10px] font-medium">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
              <input type="checkbox" id="modulable" checked={formData.modulable}
                onChange={(e) => setFormData({ ...formData, modulable: e.target.checked })}
                className="w-4 h-4 accent-[#8B0000]" data-testid="input-modulable" />
              <div>
                <Label htmlFor="modulable" className="text-sm font-medium cursor-pointer">Table modulable</Label>
                <p className="text-xs text-stone-500">Peut etre fusionnee avec d'autres tables</p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#8B0000] hover:bg-[#6B0000]" data-testid="save-table-btn">
                <Check size={16} className="mr-1" /> {editingTable ? 'Modifier' : 'Creer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">QR Code - Table {selectedTableQR?.table_number}</DialogTitle>
          </DialogHeader>
          {selectedTableQR && (
            <div className="flex flex-col items-center space-y-4 py-4">
              <img src={getTableQRImage(selectedTableQR.id)} alt={`QR Table ${selectedTableQR.table_number}`}
                className="w-56 h-56 border-4 border-stone-200 rounded-xl" />
              <p className="text-xs text-stone-500 text-center">
                Scannez pour acceder au menu de la table {selectedTableQR.table_number}
              </p>
              <Button onClick={() => downloadQRCode(selectedTableQR)} className="w-full bg-blue-600 hover:bg-blue-700">
                <Download className="mr-2" size={16} /> Telecharger le QR Code
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TablesManagement;
